import { Pressable, View } from 'react-native';
import { cn } from '@/lib/utils';
import { Text } from '@/components/ui/text';
import { Icon } from '@/features/global/components/icon/Icon';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useColor } from '~/global/hooks/useColor';
import { useRightSidebar } from '~/layout/contexts/right-sidebar-layout/RightSidebarLayout';

/**
 * The always-docked 72px icon rail. Each tab is a 40x40 icon button: the active
 * one gets a filled background + full opacity (the affordance to collapse —
 * click it again), the rest are dimmed. Each icon shows its label as a tooltip
 * on hover (web). Rendered inside the fixed-width rail column of `SidebarLayout`.
 */
export const RightSidebarRail = () => {
  const { tabs, activeKey, setActiveTab } = useRightSidebar();
  const iconColor = useColor('text-primary');

  if (!tabs) {
    return null;
  }

  return (
    <View className="h-full items-center gap-y-4 bg-surface p-2 pt-24">
      {tabs.map((tab) => {
        const isActive = tab.key === activeKey;
        return (
          <Tooltip key={tab.key} delayDuration={200}>
            <TooltipTrigger asChild>
              <Pressable
                testID={`right-sidebar-tab-${tab.key}`}
                collapsable={false}
                accessibilityRole="button"
                accessibilityLabel={tab.label}
                accessibilityState={{ selected: isActive }}
                onPress={() => setActiveTab(tab.key)}
                className={cn(
                  'h-8 w-8 items-center justify-center rounded-lg',
                  isActive
                    ? 'bg-surface-elevated'
                    : 'opacity-60 web:hover:opacity-100',
                )}
              >
                <Icon name={tab.icon} size={20} color={iconColor} />
              </Pressable>
            </TooltipTrigger>
            <TooltipContent side="left">
              <Text>{tab.label}</Text>
            </TooltipContent>
          </Tooltip>
        );
      })}
    </View>
  );
};
